/**
 * YUG360-MIND persona model — design by persona, enforce by RBAC.
 *
 * Persona → Goals → Landing analytics → Questions → Drill-down → Actions → Authority
 *
 * A persona is not the same as a role: role = permission; persona = experience + decisions needed.
 */

import { disclosureRank, maxDisclosureOf, navItemAllowed } from "./master";
import { focusHref } from "./analytics";
import { withApp } from "./story";

/** Canonical personas (stable IDs). */
export const WORKSPACE_GROUPS = {
  executive: {
    id: "executive",
    label: "Executive Workspace",
    roles: ["Executive Sponsor", "CIO", "CFO / Finance Approver", "Business Unit Owner", "Portfolio Owner"],
  },
  modernization: {
    id: "modernization",
    label: "Modernization Workspace",
    roles: ["Business Owner", "Product Owner", "Application Owner", "Business Analyst", "Program Manager", "Program Sponsor"],
  },
  architecture: {
    id: "architecture",
    label: "Architecture Workspace",
    roles: ["Enterprise Architect", "Solution Architect", "Data Architect", "Integration / Platform Architect", "Security Architect"],
  },
  delivery: {
    id: "delivery",
    label: "Delivery Workspace",
    roles: ["Project Manager", "Engineering Lead", "Developer", "QA / Test Lead", "DevOps / Platform Engineer", "SRE / Operations", "Modernization Lead"],
  },
  governance: {
    id: "governance",
    label: "Governance Workspace",
    roles: ["PMO / Governance Manager", "Risk / Compliance Officer", "Auditor", "FinOps / Finance Analyst"],
  },
  administration: {
    id: "administration",
    label: "Administration",
    roles: ["Platform Admin", "Tenant Admin", "Support Admin"],
  },
};

export const PERSONAS = {
  executive: {
    id: "executive",
    label: "CEO / Executive",
    mainQuestion: "Are modernization investments producing business value?",
    goals: ["See investment vs value", "Spot decisions and risks early", "Drill to programme or candidate without technical noise"],
    kicker: "EXECUTIVE VIEW",
    landingScope: "portfolio",
    charts: ["progress", "investment_value", "top_risks", "decisions_required"],
    drillRoutes: ["/", "/delivery", "/program"],
    permittedActions: ["view_kpis", "drill_summary", "request_review"],
    forbiddenActions: ["authorize_mdr", "mutate_evidence", "pass_gates"],
  },
  cio: {
    id: "cio",
    label: "CIO / CTO",
    mainQuestion: "What should we modernize, prioritize, fund, or stop?",
    goals: ["Prioritize candidates", "Balance investment and risk", "Fund or stop programmes"],
    kicker: "CIO VIEW",
    landingScope: "portfolio",
    charts: ["candidate_funnel", "investment_value", "top_risks", "decisions_required"],
    drillRoutes: ["/", "/applications", "/program", "/delivery"],
    permittedActions: ["view_kpis", "prioritize_portfolio", "drill_program"],
    forbiddenActions: ["authorize_mdr", "mutate_evidence"],
  },
  business_owner: {
    id: "business_owner",
    label: "Business Owner",
    mainQuestion: "Why does this application need modernization and what outcome do I expect?",
    goals: ["Confirm WHY and outcomes", "Track value at G8", "Approve business gates"],
    kicker: "VALUE VIEW",
    landingScope: "application",
    charts: ["why_outcomes", "value_progress", "g1_status"],
    drillRoutes: ["/overview", "/assessment", "/delivery"],
    permittedActions: ["confirm_why", "view_outcomes", "close_g8"],
    forbiddenActions: ["fabricate_technical_evidence"],
  },
  portfolio_owner: {
    id: "portfolio_owner",
    label: "Portfolio Owner",
    mainQuestion: "Which applications should be prioritized across my portfolio?",
    goals: ["Rank candidates", "See cross-app dependencies", "Balance capacity"],
    kicker: "PORTFOLIO VIEW",
    landingScope: "portfolio",
    charts: ["candidate_funnel", "pipeline", "top_risks"],
    drillRoutes: ["/applications", "/program", "/"],
    permittedActions: ["view_portfolio", "drill_application"],
    forbiddenActions: ["authorize_mdr"],
  },
  program_manager: {
    id: "program_manager",
    label: "Program Manager",
    mainQuestion: "Are applications, dependencies, waves, risks and milestones on track?",
    goals: ["Track milestones and RAID", "Manage waves and dependencies", "Surface blockers"],
    kicker: "PROGRAM VIEW",
    landingScope: "program",
    charts: ["milestones", "dependencies", "g1_g2_funnel", "blocked_owners"],
    drillRoutes: ["/program", "/lifecycle", "/governance"],
    permittedActions: ["view_program", "ingest_dependencies", "dispatch_alerts"],
    forbiddenActions: ["change_authorized_mdr"],
  },
  decision_owner: {
    id: "decision_owner",
    label: "Product Owner",
    mainQuestion: "What decision must I seal next, with what evidence?",
    goals: ["Seal or refuse MDR", "Confirm WHY before G1", "Own decision queue"],
    kicker: "DECISION WORKSPACE",
    landingScope: "portfolio",
    charts: ["decisions_required", "g1_funnel", "evidence_gaps"],
    drillRoutes: ["/decision", "/assessment", "/cockpit"],
    permittedActions: ["authorize_mdr", "decide_g1", "write_why"],
    forbiddenActions: ["ai_authorize"],
  },
  enterprise_architect: {
    id: "enterprise_architect",
    label: "Enterprise Architect",
    mainQuestion: "What is the right modernization strategy and target-state direction?",
    goals: ["Compare 7R options", "Own G3 architecture", "Assess portfolio technical risk"],
    kicker: "ARCHITECTURE VIEW",
    landingScope: "application",
    charts: ["tech_risk", "options", "architecture_decisions", "evidence_confidence"],
    drillRoutes: ["/decision", "/governance", "/enterprise", "/cockpit"],
    permittedActions: ["authorize_mdr", "advance_g3", "compare_options"],
    forbiddenActions: ["override_conflicts_silently"],
  },
  solution_architect: {
    id: "solution_architect",
    label: "Solution Architect",
    mainQuestion: "How should this specific application be designed and modernized?",
    goals: ["Define HLD/ADR direction", "Review suitability vs eligibility", "Guide implementation constraints"],
    kicker: "SOLUTION VIEW",
    landingScope: "application",
    charts: ["assessment_quality", "tech_risk", "options", "dependencies"],
    drillRoutes: ["/cockpit", "/decision", "/assessment"],
    permittedActions: ["advance_g6", "view_l4_evidence"],
    forbiddenActions: ["authorize_business_value"],
  },
  security_architect: {
    id: "security_architect",
    label: "Security Architect",
    mainQuestion: "Are security risks and controls acceptable?",
    goals: ["Review security evidence", "Pass or block G5", "Track exceptions"],
    kicker: "SECURITY VIEW",
    landingScope: "application",
    charts: ["security_gaps", "exceptions", "cve_status"],
    drillRoutes: ["/delivery", "/evidence", "/governance"],
    permittedActions: ["advance_g5", "review_security_evidence"],
    forbiddenActions: ["approve_unrelated_gates"],
  },
  developer: {
    id: "developer",
    label: "Developer",
    mainQuestion: "What exactly should I build and which architecture/coding rules apply?",
    goals: ["See assigned work", "Follow ADR/HLD constraints", "Fix violations"],
    kicker: "ENGINEERING VIEW",
    landingScope: "application",
    charts: ["assigned_work", "violations", "standards"],
    drillRoutes: ["/delivery", "/governance"],
    permittedActions: ["view_stories", "view_standards"],
    forbiddenActions: ["change_approved_architecture", "authorize_gates"],
  },
  tester: {
    id: "tester",
    label: "Tester",
    mainQuestion: "What needs to be tested based on risk, architecture and requirements?",
    goals: ["Risk-driven test coverage", "Track defects and exit criteria", "Validate cutover readiness"],
    kicker: "QUALITY VIEW",
    landingScope: "application",
    charts: ["test_readiness", "risk_coverage", "defects"],
    drillRoutes: ["/delivery", "/governance"],
    permittedActions: ["view_test_plan", "record_defects"],
    forbiddenActions: ["approve_production"],
  },
  auditor: {
    id: "auditor",
    label: "Auditor",
    mainQuestion: "Who decided what, why, using which evidence?",
    goals: ["Reconstruct decision lineage", "Verify provenance", "Read-only audit trail"],
    kicker: "AUDIT VIEW",
    landingScope: "portfolio",
    charts: ["decision_lineage", "evidence_provenance"],
    drillRoutes: ["/governance", "/decision", "/evidence"],
    permittedActions: ["view_lineage", "export_audit"],
    forbiddenActions: ["mutate_evidence", "mutate_decisions"],
  },
  tenant_admin: {
    id: "tenant_admin",
    label: "Tenant Admin",
    mainQuestion: "How is this customer's organization, governance and access configured?",
    goals: ["Configure org and roles", "Manage users and integrations", "Set governance policy"],
    kicker: "TENANT CONSOLE",
    landingScope: "control_plane",
    charts: [],
    drillRoutes: ["/admin/tenant"],
    permittedActions: ["configure_tenant", "invite_users"],
    forbiddenActions: ["authorize_mdr", "pass_gates"],
  },
  application_owner: {
    id: "application_owner",
    label: "Application Owner",
    mainQuestion: "What is the status and outcome for my application?",
    goals: ["Track application progress", "Understand blockers", "Escalate to decision authority"],
    kicker: "APPLICATION VIEW",
    landingScope: "application",
    charts: ["lifecycle", "blockers"],
    drillRoutes: ["/overview", "/lifecycle", "/delivery"],
    permittedActions: ["view_application"],
    forbiddenActions: ["authorize_mdr", "pass_g3_g6"],
  },
};

/** Role → persona (first match). Extend when new roles are seeded. */
const ROLE_PERSONA_MAP = [
  ["Executive Sponsor", "executive"],
  ["CIO", "cio"],
  ["Business Owner", "business_owner"],
  ["Product Owner", "decision_owner"],
  ["Program Manager", "program_manager"],
  ["Modernization Lead", "program_manager"],
  ["Enterprise Architect", "enterprise_architect"],
  ["Solution Architect", "solution_architect"],
  ["Security Architect", "security_architect"],
  ["Developer", "developer"],
  ["Tester", "tester"],
  ["Auditor", "auditor"],
  ["Tenant Admin", "tenant_admin"],
  ["Platform Admin", "tenant_admin"],
  ["App Owner", "application_owner"],
];

export function resolvePersona(guideOrRole, access) {
  const role = typeof guideOrRole === "string" ? guideOrRole : guideOrRole?.role;
  if (access?.persona_id && PERSONAS[access.persona_id]) {
    return { ...PERSONAS[access.persona_id], role: role || "Unknown", workspace: access.workspace };
  }
  const hit = ROLE_PERSONA_MAP.find(([r]) => r === role);
  const id = hit?.[1] || "decision_owner";
  const workspace = access?.workspace || workspaceForRole(role);
  return { ...PERSONAS[id], role: role || "Unknown", workspace };
}

export function workspaceForRole(role) {
  for (const group of Object.values(WORKSPACE_GROUPS)) {
    if (group.roles.includes(role)) return group.id;
  }
  if (role === "CIO" || role === "Executive Sponsor") return "executive";
  if (role === "Platform Admin" || role === "Tenant Admin") return "administration";
  return "modernization";
}

export function workspaceLabel(workspaceId) {
  return WORKSPACE_GROUPS[workspaceId]?.label || "Workspace";
}

function parseMoney(v) {
  if (!v || v === "$0") return 0;
  const s = String(v).replace("$", "").replace(",", "").trim();
  if (s.endsWith("M")) return Math.round(parseFloat(s) * 1_000_000);
  if (s.endsWith("K")) return Math.round(parseFloat(s) * 1_000);
  const n = parseFloat(s);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

function fmtMoney(n) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1000)}K`;
  return `$${n}`;
}

function derivePortfolioMetrics(portfolio) {
  const kpis = portfolio?.kpis || {};
  const apps = portfolio?.applications || [];
  const g1Approved = kpis.g1_approved ?? apps.filter((a) => a.g1_passed || a.stage?.includes("G1")).length;
  const highRisk = kpis.high_risk ?? apps.filter((a) => (a.blocked_gates || 0) > 0 || a.mdr_state === "REVIEW_READY").length;
  const investment = kpis.total_investment || kpis.expected_savings;
  return {
    programName: portfolio?.tenant?.project || "Modernization programme",
    tenantName: portfolio?.tenant?.name || "Tenant",
    totalApps: kpis.total ?? apps.length,
    g1Approved,
    decisionsRequired: kpis.awaiting_decision ?? 0,
    highRisk,
    investment: investment || fmtMoney(apps.reduce((s, a) => s + parseMoney(a.expected_savings), 0)),
    expectedSavings: kpis.expected_savings || "—",
    valuePct: kpis.value_achievement_pct,
    blockedGates: kpis.blocked_gates ?? 0,
    inWave: kpis.in_wave ?? 0,
    authorized: kpis.authorized ?? 0,
    unknownCritical: kpis.unknown_critical ?? 0,
  };
}

/** Portfolio-level KPI tiles per persona. */
export function buildPersonaPortfolioLanding(portfolio, guide) {
  const persona = resolvePersona(guide);
  const m = derivePortfolioMetrics(portfolio);
  const canDrill = guide?.can_drill !== false;

  const tilesByPersona = {
    executive: [
      { id: "program", label: "Programme", value: m.programName, detail: m.tenantName, focus: "insights", tone: "blue" },
      { id: "apps", label: "Applications", value: m.totalApps, detail: "in portfolio", focus: "actions", tone: "cyan" },
      { id: "g1", label: "G1 approved", value: m.g1Approved, detail: "candidates proceed", focus: "insights", tone: "green" },
      { id: "decisions", label: "Decisions required", value: m.decisionsRequired, detail: "human authority", focus: "actions", tone: "amber" },
      { id: "risk", label: "High attention", value: m.highRisk, detail: "blocked or pending", focus: "actions", tone: "rose" },
      { id: "invest", label: "Expected savings", value: m.expectedSavings, detail: "portfolio forecast", focus: "insights", tone: "green" },
      ...(m.valuePct != null
        ? [{ id: "value-pct", label: "Value realized", value: `${m.valuePct}%`, detail: "of targeted savings", focus: "insights", tone: "green" }]
        : []),
    ],
    cio: [
      { id: "apps", label: "Applications", value: m.totalApps, detail: `${m.g1Approved} G1 approved`, focus: "insights", tone: "blue" },
      { id: "decisions", label: "Fund / decide", value: m.decisionsRequired, detail: "awaiting MDR", focus: "actions", tone: "amber" },
      { id: "risk", label: "High-risk items", value: m.highRisk, detail: "need attention", focus: "actions", tone: "rose" },
      { id: "invest", label: "Investment envelope", value: m.investment, detail: `save ${m.expectedSavings}`, focus: "insights", tone: "cyan" },
      ...(m.valuePct != null
        ? [{ id: "value-pct", label: "Value realized", value: `${m.valuePct}%`, detail: "of targeted savings", focus: "insights", tone: "green" }]
        : []),
      { id: "wave", label: "In active change", value: m.inWave, detail: "wave assigned", focus: "insights", tone: "green" },
      { id: "retire", label: "Retain / retire", value: (portfolio?.kpis?.retain_count || 0) + (portfolio?.kpis?.retire_count || 0), detail: "rationalization", focus: "insights", tone: "slate" },
    ],
    portfolio_owner: [
      { id: "apps", label: "Candidates", value: m.totalApps, detail: "inventory + programme", focus: "actions", tone: "blue" },
      { id: "g1", label: "G1 approved", value: m.g1Approved, detail: "ready for assessment", focus: "insights", tone: "green" },
      { id: "decisions", label: "Prioritize next", value: m.decisionsRequired, detail: "decisions open", focus: "actions", tone: "amber" },
      { id: "blocked", label: "Blocked", value: m.blockedGates, detail: "gates holding", focus: "actions", tone: "rose" },
    ],
    program_manager: [
      { id: "apps", label: "Applications", value: m.totalApps, detail: "in programme", focus: "actions", tone: "blue" },
      { id: "g1", label: "G1 funnel", value: m.g1Approved, detail: `of ${m.totalApps} approved`, focus: "insights", tone: "green" },
      { id: "decisions", label: "G2 pending", value: m.decisionsRequired, detail: "MDR queue", focus: "actions", tone: "amber" },
      { id: "wave", label: "Waves active", value: m.inWave, detail: "execution", focus: "insights", tone: "cyan" },
      { id: "blocked", label: "Blocked owners", value: m.blockedGates, detail: "RAID drivers", focus: "actions", tone: "rose" },
    ],
    decision_owner: [
      { id: "decisions", label: "Your decisions", value: m.decisionsRequired, detail: "MDR / G1 queue", focus: "actions", tone: "amber" },
      { id: "g1", label: "G1 approved", value: m.g1Approved, detail: "programme progress", focus: "insights", tone: "green" },
      { id: "unknown", label: "Evidence gaps", value: m.unknownCritical, detail: "critical UNKNOWN", focus: "actions", tone: "rose" },
      { id: "apps", label: "Applications", value: m.totalApps, detail: "decision units", focus: "actions", tone: "blue" },
    ],
    auditor: [
      { id: "sealed", label: "MDRs sealed", value: m.authorized, detail: "immutable records", focus: "insights", tone: "green" },
      { id: "decisions", label: "Open decisions", value: m.decisionsRequired, detail: "awaiting human", focus: "actions", tone: "amber" },
      { id: "apps", label: "Applications", value: m.totalApps, detail: "lineage scope", focus: "insights", tone: "blue" },
    ],
  };

  const tiles = (tilesByPersona[persona.id] || tilesByPersona.decision_owner).map((t) => ({
    ...t,
    href: canDrill ? focusHref(t.focus) : null,
  }));

  const nextActions = {
    executive: { label: "Review decisions required", detail: "Drill into items needing executive attention — not Java stack traces.", route: focusHref("actions"), focus: "actions" },
    cio: { label: "Prioritize modernization candidates", detail: "Compare G1-approved apps, investment and risk before funding.", route: focusHref("insights"), focus: "insights" },
    portfolio_owner: { label: "Open application prioritization", detail: "Rank candidates across the portfolio.", route: "/applications", focus: "actions" },
    program_manager: { label: "Review programme blockers", detail: "Dependencies, waves and RAID items needing owners.", route: "/program", focus: "actions" },
    decision_owner: { label: "Seal or refuse pending MDR", detail: "AI cannot authorize. Named human authority required.", route: guide?.detail_route || "/decision", focus: "actions" },
    auditor: { label: "Trace decision lineage", detail: "Read-only provenance from strategy to value.", route: "/governance", focus: "insights" },
  };

  return {
    persona,
    mainQuestion: persona.mainQuestion,
    tiles,
    nextAction: nextActions[persona.id] || nextActions.decision_owner,
    charts: persona.charts,
    showTechnicalCharts: disclosureRank(maxDisclosureOf(guide)) >= 3,
  };
}

/** Application-level KPI tiles per persona (e.g. BLACK). */
export function buildPersonaAppLanding(app, guide) {
  if (!app) return null;
  const persona = resolvePersona(guide);
  const deps = app.dependencies?.nodes?.length ?? app.passport?.Dependencies?.match(/\d+/)?.[0] ?? 0;
  const criticalDeps = (app.dependencies?.nodes || []).filter((n) => n.kind === "critical").length;
  const mdr = (app.mdrs || []).slice(-1)[0] || {};
  const g1Gate = (app.gates || []).find((g) => g.id === "G1") || {};
  const risks = app.risks || [];
  const highRisks = risks.filter((r) => r.severity === "high" || r.severity === "critical").length;

  const tilesByPersona = {
    enterprise_architect: [
      { label: "Technology risk", value: highRisks ? "HIGH" : "MEDIUM", detail: `${risks.length} risks tracked`, tone: highRisks ? "rose" : "amber" },
      { label: "Architecture fit", value: app.assessments?.["7c"]?.[0]?.score >= 70 ? "STRONG" : "PARTIAL", detail: "7C assessment", tone: "cyan" },
      { label: "Evidence confidence", value: `${app.confidence || 0}%`, detail: "canonical + verified", tone: "green" },
      { label: "Dependencies", value: deps, detail: `${criticalDeps} critical`, tone: "blue", drill: "dependencies" },
      { label: "MDR", value: mdr.state || "PENDING", detail: mdr.version || "draft", tone: mdr.state === "AUTHORIZED" ? "green" : "amber" },
    ],
    solution_architect: [
      { label: "Suitability", value: app.alternatives?.find((a) => a.recommended)?.strategy?.slice(0, 12) || "—", detail: "recommended option", tone: "cyan" },
      { label: "Confidence", value: `${app.confidence || 0}%`, detail: "not authorization", tone: "green" },
      { label: "Dependencies", value: deps, detail: "blast radius", tone: "blue", drill: "dependencies" },
      { label: "G1", value: g1Gate.state || "—", detail: "intent sealed?", tone: g1Gate.state === "PASS" ? "green" : "amber" },
    ],
    security_architect: [
      { label: "Open CVEs", value: app.before?.pain?.find((p) => p.includes("CVE")) ? "4 critical" : risks.length, detail: "security scan", tone: "rose" },
      { label: "G5 gate", value: (app.gates || []).find((g) => g.id === "G5")?.state || "PENDING", detail: "security sign-off", tone: "amber" },
      { label: "Classification", value: app.passport?.Security?.split("·")[0]?.trim() || "Review", detail: "compliance scope", tone: "cyan" },
    ],
    developer: [
      { label: "Stories assigned", value: (app.work_packages || []).length || 6, detail: "governed work", tone: "blue" },
      { label: "Architecture violations", value: 2, detail: "ADR/HLD drift", tone: "rose" },
      { label: "Security issues", value: 1, detail: "open findings", tone: "amber" },
      { label: "Tests failing", value: 3, detail: "risk-driven suite", tone: "rose" },
    ],
    tester: [
      { label: "Test readiness", value: (app.gates || []).find((g) => g.id === "G6")?.state || "PENDING", detail: "G6 exit criteria", tone: "amber" },
      { label: "Risk coverage", value: "71%", detail: "architecture-linked", tone: "cyan" },
      { label: "Defects open", value: 3, detail: "pre-production", tone: "rose" },
    ],
    business_owner: [
      { label: "Primary WHY", value: app.why?.driver?.slice(0, 24) || "—", detail: "business driver", tone: "cyan" },
      { label: "Outcomes", value: (app.why?.outcomes || []).length, detail: "expected results", tone: "green" },
      { label: "G8 value", value: app.value?.actual_savings ? "In progress" : "Not closed", detail: app.value?.kpi_realization || "awaiting proof", tone: "amber" },
    ],
    application_owner: [
      { label: "Stage", value: app.stage || "—", detail: app.recommendation || "7R pending", tone: "blue" },
      { label: "Next gate", value: app.pending_gate || "G2", detail: app.pending_by || "—", tone: "amber" },
    ],
  };

  const standards = {
    developer: ["ADR-014", "HLD-002", "Java 21 Standard", "API Standard", "Logging Standard"],
    solution_architect: ["Target architecture", "NFR baseline", "Integration contracts"],
    enterprise_architect: ["7R comparison", "Portfolio standards", "Landing zone policy"],
  };

  const nextByPersona = {
    enterprise_architect: { label: "Review modernization alternatives", detail: "Compare 7R options with evidence gaps before MDR.", route: "/decision" },
    solution_architect: { label: "Open decision cockpit", detail: "Suitability is not eligibility — confidence is not authorization.", route: "/cockpit" },
    security_architect: { label: "Review security evidence", detail: "UNKNOWN CVE is not a yes. G5 requires named human.", route: "/evidence" },
    developer: { label: "Resolve API idempotency violation", detail: "Applicable standards and ADRs must be satisfied before merge.", route: "/governance" },
    tester: { label: "Complete risk-driven test plan", detail: "Cover integration, security and rollback paths.", route: "/delivery" },
    business_owner: { label: "Confirm outcomes before G8", detail: "Deployment alone is not success.", route: "/delivery" },
  };

  const tileSet = tilesByPersona[persona.id];
  if (!tileSet && persona.landingScope !== "application") return null;

  return {
    persona,
    title: `${app.name} — ${persona.label}`,
    tiles: tileSet || tilesByPersona.enterprise_architect,
    applicableStandards: standards[persona.id] || [],
    nextAction: nextByPersona[persona.id] || null,
    route: (r) => withApp(r, app.id),
  };
}

export function personaDrillAllowed(route, guide) {
  const persona = resolvePersona(guide);
  const base = route.split("?")[0];
  return persona.drillRoutes.some((r) => base === r || base.startsWith(r)) && navItemAllowed(base, guide);
}
